import React, { useRef, useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  GeneralStyle,
  SectionWrapper,
  ContentHeader,
  ContentTitle,
  ContentForm,
  SelectField,
  TextField,
  ButtonCancel,
  ButtonCreateLob,
  ButtonContainer,
} from "./LobCreationForm.styled";
import { Row, Col, FormControl, Form } from "react-bootstrap";
import { useMutation, useQuery } from "@apollo/client";
import { theme as themeConfig } from "../../themes/themes";
import { CREATE_LOB, UPDATE_LOB } from "../../GraphQL/Mutations";
import { NotificationManager } from "react-notifications";
import PointersLine from "../Loaders/PointersLine/PointersLine";
import { COUNTRIES_BY_CLIENT, CURRENT_USER_INFO } from "../../GraphQL/Queries";
import BackButtonComponent from "../BackButtonComponent/BackButtonComponent";
import Select from "react-select";
import LockIcon from "@mui/icons-material/Lock";
import { Divider } from "@mui/material";
import { ROLES } from "../../util/RolesConstants";

const customStyles = {
  control: (base, state) => ({
    ...base,
    boxShadow: "none",
    borderColor: themeConfig.primary.icon,
    // You can also use state.isFocused to conditionally style based on the focus state
    "&:hover": {
      borderColor: themeConfig.primary.blue,
      boxShadow: "0 0 0 0.2rem rgb(0 123 255 / 25%)",
    },
  }),
};

const LobCreationForm = ({
  user,
  setFormTrigger,
  editRowData,
  setReloadRender,
}) => {
  const rSelectedCountry = useRef();
  const [allCountries, setAllCountries] = useState([]);
  const [countryId, setCountryId] = useState();
  const rName = useRef();
  const rDescription = useRef();
  const [createLob, { loading: loadingCreateLob }] = useMutation(CREATE_LOB);
  const [updateLob, { loading: loadingUpdateLob }] = useMutation(UPDATE_LOB);
  const { data: countries } = useQuery(COUNTRIES_BY_CLIENT, {
    variables: { clientId: user.clientId },
    context: { clientName: "LinkUsers" },
  });
  const { data: userdata } = useQuery(CURRENT_USER_INFO, {
    variables: { userId: user.userId },
    context: { clientName: "LinkUsers" },
  });
  let handleSaveLob = () => {
    setReloadRender(true);
    let name = rName.current.value;
    let description = rDescription?.current?.value
      ? rDescription?.current?.value
      : "";
    let username = user.userName;
    if (name && username && countryId) {
      createLob({
        variables: {
          lobModel: {
            name: name,
            description: description,
            //languageId: 4,
            countryId: countryId,
          },
        },
        context: { clientName: "LinkUsers" },
      })
        .then((response) => {
          let err = response.data.createLob.withError;
          if (response.data.createLob?.lobId) {
            NotificationManager.success(
              "Your LOB was successfully created",
              "",
              5000
            );
            setFormTrigger("");
            setReloadRender(false);
          } else {
            NotificationManager.error(
              response.data.createLob.message,
              "",
              5000
            );
            setFormTrigger("");
          }
        })
        .catch((error) => {
          const errorMessage = error.message.split("\n")[0];
          NotificationManager.error(errorMessage, "", 5000);
        });
    } else {
      NotificationManager.warning(
        "Please enter all fields to create your LOB",
        "",
        5000
      );
    }
  };
  let handleUpdateLob = () => {
    setReloadRender(true);
    let lobid = editRowData.lobid;
    let name = rName.current.value;
    let description = rDescription?.current?.value
      ? rDescription?.current?.value
      : "";
    if (lobid && name) {
      updateLob({
        variables: {
          lobModel: {
            lobId: lobid,
            name: name,
            description: description,
          },
        },
        context: { clientName: "LinkUsers" },
      })
        .then((response) => {
          let err = response.data.updateLob.message;
          if (response.data.updateLob?.lobId) {
            NotificationManager.success(
              "Your LOB was successfully updated",
              "",
              5000
            );
            setFormTrigger("");
            setReloadRender(false);
          } else {
            NotificationManager.error(err.message, "", 5000);
            setFormTrigger("");
          }
        })
        .catch((error) => {
          const errorMessage = error.message.split("\n")[0];
          NotificationManager.error(errorMessage, "", 5000);
        });
    } else {
      NotificationManager.warning(
        "Please enter all fields to update your LOB",
        "",
        5000
      );
    }
  };
  let handleCountrySelected = (countryId) => {
    setCountryId(countryId);
  };
  let showTitle = () => {
    if (editRowData) {
      return <label className="title">Update LOB</label>;
    }
    return <label className="title">Create LOB</label>;
  };
  const goBack = () => {
    setFormTrigger("");
  };
  useEffect(() => {
    if (countries?.countryLocationList) {
      setAllCountries(
        countries?.countryLocationList.map((data) => {
          return {
            value: data.countryId,
            label: data.countryName,
          };
        })
      );
      if (editRowData?.countryName) {
        if ([ROLES.SUPER_ADMIN, ROLES.GAM].includes(user.roleId)) {
          let country = countries.countryLocationList.find(
            (data) => data.countryName === editRowData.countryName
          );
          setCountryId(country?.countryId);
        }
      }
    }
  }, [countries]);
  useEffect(() => {
    if (!user) {
      return;
    }
    if ([ROLES.COUNTRY_ADMIN, ROLES.SUPERVISOR].includes(user.roleId)) {
      setCountryId(userdata?.currentUser?.location?.countryId);
    }
  }, [userdata]);
  return (
    <>
      <GeneralStyle>
        {(loadingUpdateLob || loadingCreateLob) && (
          <PointersLine color={themeConfig.text.primary} left={"50%"} />
        )}
        {!loadingUpdateLob && !loadingCreateLob && (
          <>
            <br />
            <BackButtonComponent
              backtitle={"Site Management"}
              locationtitle={"LOB"}
              createTitle={!editRowData ? "Create LOB" : "Update LOB"}
              goToBackPage={goBack}
              identity={"createUpdateLob"}
            />
            <ContentHeader>
              <ContentTitle>
                {showTitle()}
                <Divider orientation="vertical" />
              </ContentTitle>
            </ContentHeader>
            <ContentForm>
              {(user.roleId === ROLES.SUPER_ADMIN ||
                user.roleId === ROLES.GAM) && (
                  <SelectField hidden={editRowData ? true : false}>
                    <Form.Label>Location</Form.Label>
                    <label className="mandatory-info">*</label>
                    <Select
                      styles={customStyles}
                      placeholder="Please select an option"
                      ref={rSelectedCountry}
                      options={allCountries}
                      onChange={(e) => handleCountrySelected(e.value)}
                      defaultInputValue={editRowData?.countryName}
                    />
                  </SelectField>
                )}
              {(user.roleId === ROLES.COUNTRY_ADMIN ||
                user.roleId === ROLES.SUPERVISOR) && (
                  <TextField>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <div>
                        <Form.Label>Location</Form.Label>
                        <label className="mandatory-info">*</label>
                      </div>
                      <div style={{ position: "relative" }}>
                        <input
                          style={{ backgroundColor: themeConfig.neutral.gray[200] }}
                          type="text"
                          defaultValue={
                            userdata?.currentUser?.location?.countryName
                          }
                          disabled
                        />
                        <LockIcon className="lock-icon" />
                      </div>
                    </div>
                  </TextField>
                )}
              <TextField>
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <div>
                    <Form.Label>LOB Name</Form.Label>
                    <label className="mandatory-info">*</label>
                  </div>
                  <input
                    type="text"
                    defaultValue={editRowData?.name}
                    ref={rName}
                    placeholder="Type Here"
                  />
                </div>
              </TextField>
            </ContentForm>
            <ButtonContainer>
              <ButtonCancel onClick={() => setFormTrigger("")}>
                Cancel
              </ButtonCancel>
              {editRowData && !loadingUpdateLob && (
                <ButtonCreateLob onClick={() => handleUpdateLob()}>
                  Update LOB
                </ButtonCreateLob>
              )}
              {!editRowData && !loadingUpdateLob && (
                <ButtonCreateLob onClick={() => handleSaveLob()}>
                  Create LOB
                </ButtonCreateLob>
              )}
            </ButtonContainer>
            <SectionWrapper hidden={true} className="mt-4 mb-5">
              <Row>
                <Col md="12">
                  <label>Description</label>&nbsp;
                  <span style={{ color: themeConfig.status.error }}>*</span>
                </Col>
                <Col md="12">
                  <FormControl
                    as="textarea"
                    style={{ fontSize: "14px" }}
                    defaultValue={editRowData?.description}
                    ref={rDescription}
                    placeholder="Please enter a description"
                  />
                </Col>
              </Row>
            </SectionWrapper>
          </>
        )}
      </GeneralStyle>
    </>
  );
};

export default LobCreationForm;

LobCreationForm.propTypes = {
  user: PropTypes.object,
  setFormTrigger: PropTypes.func,
  editRowData: PropTypes.object,
  setReloadRender: PropTypes.func,
};